<template>
  <layout-basic>
    <page-hero
      title="Simple Pricing. No Hidden Costs."
      lead="One predictable price based on the size of your business."
    />

    <section class="demo-video-section">
      <v-container style="max-width: 960px">
        <demo-video video-key="pricing" />
      </v-container>
    </section>

    <point-grid
      title="Everything included. One price per year, fixed for three years."
      :points="pricingPoints"
      :columns="2"
      tint
    />

    <section>
      <v-container>
        <div class="price-business-wrapper text-center">
            <h2 class="font-sora">Choose the plan for your business</h2>
            <p>Buy IMPROVE online, or Experience IMPROVE first — no credit card required for the tour.</p>
        </div>
        <div class="pricing-tables">
          <!-- Desktop / Tablet Layout -->
          <div class="table-wrapper desktop-table">
            <!-- Header Row -->
            <div class="header-row">
              <div class="header-empty-cell"></div>
              <!-- Empty cell in the top-left corner -->
              <div
                v-for="(plan, index) in plans"
                :key="index"
                class="header-plan-cell"
              >
                <h3>{{ plan.name }}</h3>
                <p class="plan-users">{{ plan.users }}</p>
                <h2>{{ plan.price }}<span>*</span></h2>
                <p class="plan-year">Year 1</p>
                <p class="plan-renewal">Years 2 and 3: <strong>{{ plan.renewal }}</strong> per year</p>
                <p>{{ plan.description }}</p>
                <div class="plan-cta-row">
                  <a :href="experienceHref" class="free-trial-btn" rel="noopener">Experience IMPROVE</a>
                  <router-link :to="'/get-started?plan=' + plan.slug" class="buy-now-btn">Buy IMPROVE</router-link>
                </div>
              </div>
            </div>

            <!-- Features Rows -->
            <div
              v-for="(feature, index) in featureRows"
              :key="index"
              class="feature-row"
              :class="{ 'feature-row--investment': feature.highlight }"
            >
              <div class="feature-name-cell">{{ feature.name }}</div>
              <div
                v-for="(plan, planIndex) in plans"
                :key="planIndex"
                class="feature-value-cell"
              >
                <span
                  v-if="feature.values[planIndex] === true"
                  class="check-icon"
                  >✔</span
                >
                <span
                  v-else-if="feature.values[planIndex] === false"
                  class="dash-icon"
                  >-</span
                >
                <span v-else>{{ feature.values[planIndex] }}</span>
              </div>
            </div>
          </div>

          <!-- Mobile Layout -->
          <div class="mobile-table">
            <div
              v-for="(plan, index) in plans"
              :key="index"
              class="pricing-plan-card"
            >
              <div class="plan-header">
                <h3>{{ plan.name }}</h3>
                <p class="plan-users">{{ plan.users }}</p>
                <h2>{{ plan.price }}<span>*</span></h2>
                <p class="plan-year">Year 1</p>
                <p class="plan-renewal">Years 2 and 3: <strong>{{ plan.renewal }}</strong> per year</p>
                <p>{{ plan.description }}</p>
                <div class="plan-cta-row">
                  <a :href="experienceHref" class="free-trial-btn" rel="noopener">Experience IMPROVE</a>
                  <router-link :to="'/get-started?plan=' + plan.slug" class="buy-now-btn">Buy IMPROVE</router-link>
                </div>
              </div>
              <ul class="features-list">
                <li
                  v-for="(feature, featureIndex) in featureRows"
                  :key="featureIndex"
                  :class="{ 'feature-row--investment': feature.highlight }"
                >
                  <span>{{ feature.name }}:</span>
                  <span v-if="feature.values[index] === true" class="check-icon"
                    >✔</span
                  >
                  <span
                    v-else-if="feature.values[index] === false"
                    class="dash-icon"
                    >-</span
                  >
                  <span v-else>{{ feature.values[index] }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <p class="vat-text">*All prices exclusive of VAT; VAT at 20% is added at the checkout and itemised on your invoice. IMPROVE is provided on a three year agreement: the year 1 price, then the years 2 and 3 price in each of the following two years.{{ launchFeeNote ? ' ' + launchFeeNote : '' }}</p>
      </v-container>
    </section>
    <section>
        <div class="price-quote-wrapper">
            <h2 class="font-sora">250+ users in your business?</h2>
            <p>Request a bespoke quote from our team.</p>
            <a href="/contact" class="book-demo-btn" style="max-width: 200px;">
                Request a Quote
            </a>
        </div>
    </section>
    <section style="margin-bottom: 40px;">
      <v-row no-gutters>
        <v-col cols="12" align-self="center">
          <v-container>
            <div class="testimonial-title text-center">
              <img
                src="/static/homepage/testimonial/improve-customer-testimonials.png"
              />

              <h2>Customer Testimonials</h2>

              <p>
                What our customers say about IMPROVE
              </p>
            </div>
          </v-container>
        </v-col>
      </v-row>
      <v-container>
        <VueSlickCarousel v-bind="testimonialCarousel">
          <div class="testimonial-item">
            <div class="testimonial-card">
              <div>
                <h2>“Flexible and Limitless”</h2>
                <p>
                  “The system is like no other in the market. HRCentral Limited 
                  and our clients are continually impressed by the user-friendly, 
                  flexible and limitless training opportunities IMPROVE can offer.”
                </p>
              </div>
              <img
                src="/static/homepage/testimonial/hr-central-human-resources-consultancy.jpg"
                alt="Smart Space Approved IMPROVE partner"
              />
            </div>
          </div>
          <div class="testimonial-item">
            <div class="testimonial-card">
              <div>
                <h2>“The Best Onboarding Process”</h2>
                <p>
                  “New staff have been in ‘awe’ of our onboarding process which
                  is all centrally delivered via IMPROVE. We have found it to be
                  the best onboarding process we have experienced.”
                </p>
              </div>
              <img
                src="/static/homepage/testimonial/advocacy-focus-testimonial.png"
                alt="Advocacy Focus Approved IMPROVE partner"
              />
            </div>
          </div>
          <div class="testimonial-item">
            <div class="testimonial-card">
              <div>
                <h2>“Comprehensive & Easy to Access”</h2>
                <p>
                  “It’s authoritative, accurate, comprehensive, and easy to
                  access. It removes the stress of training delivery from
                  business owners, knowing that they can review what their staff
                  are learning.”
                </p>
              </div>
              <img
                src="/static/homepage/testimonial/smart-space-testimonial.png"
                alt="Smart Space Approved IMPROVE partner"
              />
            </div>
          </div>
          <div class="testimonial-item">
            <div class="testimonial-card">
              <div>
                <h2>“Consistently Impressed”</h2>
                <p>
                  “As a proud partner of IMPROVE, we have been consistently impressed with the 
                  customer service and support we have received from the team. They are responsive, 
                  go out of their way to help, and adapt to support our needs and growth as a HR 
                  consultancy, which in turn supports our clients.”
                </p>
              </div>
              <img
                src="/static/homepage/testimonial/hr-central-human-resources-consultancy.jpg"
                alt="Smart Space Approved IMPROVE partner"
              />
            </div>
          </div>
          <div class="testimonial-item">
            <div class="testimonial-card">
              <div>
                <h2>“Seamless Training Process”</h2>
                <p>
                  “The user-friendly interface has been built to ensure that
                  everyone can use it with ease, and the courses are tailored to
                  suit each team member's learning needs.”
                </p>
              </div>
              <img
                src="/static/homepage/testimonial/eden-holistic-pet-foods-testimonial.png"
                alt="Eden Holistic Pet Foods Approved IMPROVE partner"
              />
            </div>
          </div>
        </VueSlickCarousel>
      </v-container>
    </section>

    <standard-cta
      title="Ready to buy IMPROVE?"
      lead="Purchase IMPROVE online, or Experience IMPROVE first through the interactive tour."
    />
  </layout-basic>
</template>

<script>
import LayoutBasic from '@/layouts/Dashboard';
import { IMPROVE_CONFIG } from "@/config.js";
import PurchaseAPI from '@/Api/purchase.js';

// Years 2 and 3 prices from the September 2026 brochure. Display fallback for
// the moment before the catalogue answers (and if it never does); a catalogue
// that answers WITHOUT renewal prices keeps the whole hardcoded set instead of
// being mixed with these.
const RENEWAL_FALLBACK_PENCE = { essential: 169500, growth: 249500, unlimited: 399500 };
import { formatPounds, planDisplayName } from '@/utils/currency.js';
import { getExperienceHref } from '@/config/ctas';
import VueSlickCarousel from 'vue-slick-carousel';
import 'vue-slick-carousel/dist/vue-slick-carousel.css';
import 'vue-slick-carousel/dist/vue-slick-carousel-theme.css';
import PageHero from '@/components/marketing/PageHero.vue';
import PointGrid from '@/components/marketing/PointGrid.vue';
import StandardCta from '@/components/marketing/StandardCta.vue';
import DemoVideo from '@/components/DemoVideo.vue';
import { marketingPageMeta } from '@/utils/marketingPage';

export default {
  computed: {
    experienceHref() {
      return getExperienceHref();
    },
    // The feature matrix plus "Your Total IMPROVE Investment" (Steve, 15 Sep
    // 2026): year 1 + two years at the years 2 and 3 price + the one-off launch
    // fee, per plan, placed between the Launch Programme row and Leadership
    // Plus. Computed so it follows the catalogue when the prices come from it.
    featureRows() {
      const investment = {
        name: 'Your Total IMPROVE Investment (3 years)',
        values: this.plans.map((p) => formatPounds(p.totalPence)),
        highlight: true,
      };
      const rows = this.features.slice();
      const launchAt = rows.findIndex((f) => f.name === 'IMPROVE Launch Programme (One off Cost)');
      const lpAt = rows.findIndex((f) => /Leadership Plus/.test(f.name));
      const at = launchAt >= 0 ? launchAt + 1 : lpAt >= 0 ? lpAt : rows.length;
      rows.splice(at, 0, investment);
      return rows;
    },
  },
  data() {
    return {
      directUrl: IMPROVE_CONFIG.DIRECT_URL,
      // Price lines are a display fallback ONLY. Stripe (via the public
      // purchase catalogue) is the source of truth; fetchCatalogue()
      // replaces this array wholesale when the catalogue loads.
      // Footnote after the asterisk line; driven by the catalogue once loaded
      // (repriced fee -> new amount, withdrawn fee -> removed). This default is
      // the display fallback only.
      launchFeeNote: 'The IMPROVE Launch Programme is a one-off £695 setup fee.',
      pricingPoints: [
        '1–50 employees: Essential £995, then £1,695 a year',
        '51–100 employees: Growth £1,695, then £2,495 a year',
        '101–250 employees: Unlimited £2,995, then £3,995 a year',
        'Launch Programme £695 one-off',
        'No complicated licence structures',
        'No hidden surprises',
      ],
      testimonialCarousel: {
        infinite: true,
        autoplay: true,
        dots: true,
        speed: 300,
        slidesToShow: 3,
        slidesToScroll: 1,
        initialSlide: 0,
        responsive: [
          {
            breakpoint: 1200,
            settings: {
              slidesToShow: 2,
              slidesToScroll: 1,
            },
          },
          {
            breakpoint: 959,
            settings: {
              slidesToShow: 1,
              slidesToScroll: 1,
            },
          },
        ],
      },
      // Display fallback ONLY. Replaced wholesale from the Stripe catalogue
      // by fetchCatalogue(); prices and user bandings here are shown only
      // until it loads or when the endpoint is unavailable.
      plans: [
        {
          name: 'Essential',
          users: '1-50 USERS',
          price: '£995',
          renewal: '£1,695',
          totalPence: 508000,
          description: 'Per business, three year agreement',
          slug: 'essential',
        },
        {
          name: 'Growth',
          users: '51-100 USERS',
          price: '£1,695',
          renewal: '£2,495',
          totalPence: 738000,
          description: 'Per business, three year agreement',
          slug: 'growth',
        },
        {
          name: 'Unlimited',
          users: '101-250 USERS',
          price: '£2,995',
          renewal: '£3,995',
          totalPence: 1168000,
          description: 'Per business, three year agreement',
          slug: 'unlimited',
        },
      ],
      features: [
        {
          name: 'Learning Management System',
          values: ['Included', 'Included', 'Included'],
        },
        {
          name: 'Unlimited Courses',
          values: ['Included', 'Included', 'Included'],
        },
        {
          name: 'Unlimited Playlists',
          values: ['Included', 'Included', 'Included'],
        },
        {
          name: 'Reporting & Dashboards',
          values: ['Included', 'Included', 'Included'],
        },
        {
          name: 'Certificates',
          values: ['Included', 'Included', 'Included'],
        },
        {
          name: 'Mobile Friendly Access',
          values: ['Included', 'Included', 'Included'],
        },
        {
          name: 'User Management',
          values: ['Included', 'Included', 'Included'],
        },
        {
          name: 'Email Support',
          values: ['Included', 'Included', 'Included'],
        },
        {
          name: 'IMPROVE Launch Programme (One off Cost)',
          values: ['£695', '£695', '£695'],
        },
        {
          name: 'Bolt-On Leadership Plus (50+ Interactive Modules)',
          values: ['£1,995', '£2,995', '£3,995'],
        },
      ],
    };
  },
  name: 'Pricing',
  metaInfo() {
    return marketingPageMeta({
      title: 'Simple Pricing. No Hidden Costs. | IMPROVE',
      description:
        'Simple, transparent business pricing on a three year agreement. Essential, Growth and Unlimited from £995 in year 1, plus a one-off £695 Launch Programme.',
      path: '/pricing',
    });
  },
  components: {
    LayoutBasic,
    VueSlickCarousel,
    PageHero,
    PointGrid,
    StandardCta,
    DemoVideo,
  },
  created() {
    this.fetchCatalogue();
  },
  mounted: function() {},
  methods: {
    // Stripe (via the public purchase catalogue) is the source of truth for
    // the plan prices and banding labels shown on this page; the hardcoded
    // data() values are a display fallback kept when the endpoint is
    // unavailable. Failures are deliberately silent: visitors should never
    // see an error toast over pricing display.
    fetchCatalogue() {
      PurchaseAPI.catalogue()
        .then((res) => {
          const data = res && res.data ? res.data : {};
          const list = Array.isArray(data.plans) ? data.plans : [];
          const fetched = [];
          list.forEach((p) => {
            if (!p || !p.slug || typeof p.amount_pence !== 'number') return;
            // Years 2 and 3 price: from the catalogue when the API sends it,
            // else the September 2026 brochure figure for that plan.
            const renewalPence = typeof p.renewal_amount_pence === 'number'
              ? p.renewal_amount_pence
              : RENEWAL_FALLBACK_PENCE[p.slug] || p.amount_pence;
            fetched.push({
              name: planDisplayName(p.name),
              users: (p.users_label || '').toUpperCase(),
              price: formatPounds(p.amount_pence),
              renewal: formatPounds(renewalPence),
              amountPence: p.amount_pence,
              renewalPence,
              description: 'Per business, three year agreement',
              slug: p.slug,
              // '1-50 users' -> '1–50' (en dash) to match the existing copy.
              // users_label first: the bullets talk about employees, and the
              // banding_label ('0-50') is a contract term, not people copy.
              range: (p.users_label || p.banding_label || '')
                .replace(/\s*users\s*$/i, '')
                .replace(/-/g, '–'),
            });
          });
          if (!fetched.length) return; // malformed payload: keep the fallback
          // This page's desktop grid and feature matrix are built for exactly
          // three columns (grid-template-columns, per-row values arrays). A
          // catalogue with any other plan count keeps the fallback rather than
          // rendering a broken table; BuyNow handles arbitrary counts.
          if (fetched.length !== 3) return;

          // Every plan must carry its years 2 and 3 price. A 200 without one
          // is an API that predates the three year agreement: printing the
          // brochure's renewal figures beside it would advertise a deal that
          // API will neither contract nor charge. Keep the fallback whole.
          if (fetched.some((p) => typeof p.renewalPence !== 'number')) return;

          const feePence =
            data.launch_fee && typeof data.launch_fee.amount_pence === 'number'
              ? data.launch_fee.amount_pence
              : null;
          const fee = feePence === null ? null : formatPounds(feePence);

          // Assign whole arrays so the tiles and tables re-render reactively.
          this.plans = fetched.map((p) => ({
            name: p.name,
            users: p.users,
            price: p.price,
            renewal: p.renewal,
            // Year 1 + years 2 and 3 + the one-off fee (0 if withdrawn).
            totalPence: p.amountPence + 2 * p.renewalPence + (feePence || 0),
            description: p.description,
            slug: p.slug,
          }));
          this.pricingPoints = fetched
            .map((p) => p.range + ' employees: ' + p.name + ' ' + p.price + ', then ' + p.renewal + ' a year')
            .concat(fee ? ['Launch Programme ' + fee + ' one-off'] : [])
            .concat([
              'No complicated licence structures',
              'No hidden surprises',
            ]);
          // The fee row and footnote follow the catalogue: repriced -> new
          // amount; withdrawn (null on a SUCCESSFUL fetch) -> removed.
          if (fee) {
            this.features = this.features.map((f) =>
              f.name === 'IMPROVE Launch Programme (One off Cost)'
                ? { name: f.name, values: fetched.map(() => fee) }
                : f
            );
            this.launchFeeNote = 'The IMPROVE Launch Programme is a one-off ' + fee + ' setup fee.';
          } else {
            this.features = this.features.filter(
              (f) => f.name !== 'IMPROVE Launch Programme (One off Cost)'
            );
            this.launchFeeNote = '';
          }
        })
        .catch(() => {
          // Endpoint unavailable (e.g. 503): keep the hardcoded fallback.
        });
    },
  },
};
</script>

<style>
.price-wrapper {
  display: flex;
  align-items: center;
  height: 525px;
}

.price-banner {
  width: 234px;
  height: 20px;
  background-color: #08a0a8;
  color: #ffffff;
  font-weight: 700;
  font-size: 10px;
  letter-spacing: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 25px;
  margin-bottom: 20px;
}

p.vat-text {
    margin: 10px;
    text-align: center;
}

.price-wrapper h1 {
  font-size: 50px;
  font-weight: 700;
  margin-bottom: 20px;
}

.price-wrapper p {
  font-size: 18px;
  color: #002458;
  font-weight: 400;
  max-width: 485px;
}

.pricing-images {
  position: relative;
  height: 100%;
}

.yellow-jumper-man,
.screen-1,
.screen-2,
.screen-3 {
  position: absolute;
}

img.yellow-jumper-man {
  top: -62px;
  right: 40%;
}

img.screen-1 {
  left: -17%;
  top: 132px;
}

img.screen-2 {
  right: 10%;
}

img.screen-3 {
  left: -8%;
  top: -79px;
}

@media screen and (max-width: 959px) {
  .price-wrapper {
    flex-direction: column;
    height: 453px;
  }

  .price-banner {
    display: none;
  }

  .price-wrapper {
    text-align: center;
    padding-top: 50px;
  }

  .price-wrapper h1 {
    font-size: 1.5rem;
  }

  .price-wrapper p {
    font-size: 1rem;
    max-width: 579px;
  }

  img.yellow-jumper-man {
    width: 177px;
    top: -90px;
  }

  img.screen-1,
  img.screen-2,
  img.screen-3 {
    width: 85px;
  }

  img.screen-1 {
    left: -3%;
    top: 68px;
  }

  img.screen-2 {
    top: -25px;
    right: 17px;
  }

  img.screen-3 {
    left: 7%;
    top: -91px;
  }
}

@media screen and (max-width: 500px) {
  .price-wrapper {
    flex-direction: column;
    height: 400px;
    padding-top: 10px;
  }

  img.yellow-jumper-man {
    width: 140px;
    top: -76px;
    right: 32%;
  }

  img.screen-1 {
    width: 65px;
    left: 4%;
    top: 39px;
  }

  img.screen-2 {
    width: 65px;
    top: -25px;
    right: 17px;
  }

  img.screen-3 {
    width: 65px;
    left: 10%;
    top: -79px;
  }
}

.table-wrapper {
  display: grid;
  grid-template-columns: 1fr repeat(3, 1fr); /* 1 column for features, 3 columns for plans */
  gap: 1px;
  background-color: #ddd; /* Grid gap color */
}

.desktop-table {
  display: grid;
}

.header-row {
  display: contents; /* Makes rows line up with grid */
}

.header-empty-cell {
  background: #ffffff;
}

.header-plan-cell {
  background: white;
  text-align: center;
  padding: 30px 15px;
  border-top: 1px solid #ddd;;
}

.plan-header h3,
.header-plan-cell h3 {
    font-size: 16px;
    font-weight: 700;
    color: #333333;
}

.plan-header .plan-users,
.header-plan-cell .plan-users {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.06em;
    color: #08a0a8;
    margin: 4px 0 8px;
}

.plan-header h2 span,
.header-plan-cell h2 span {
    position: absolute;
}

.plan-header h2,
.header-plan-cell h2 {
    font-size: 35px;
    font-weight: 700;
    color: #08a0a8;
}

.plan-header p,
.header-plan-cell p {
    font-size: 13px;
    font-weight: 400;
    color: #333333;
}

.plan-year {
  margin: -6px 0 2px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--brand-grey, #6b7280);
}

.plan-renewal {
  margin: 0 0 8px;
  font-size: 13px;
  color: var(--brand-navy, #002458);
}

/* The 3 year total: the one row Steve wants a visitor to see first. */
.feature-row--investment {
  background: #f3fbfb;
  border-top: 2px solid var(--brand-teal, #08a0a8);
  border-bottom: 2px solid var(--brand-teal, #08a0a8);
}

.feature-row--investment .feature-name-cell,
.feature-row--investment .feature-value-cell span,
li.feature-row--investment {
  font-weight: 800;
  color: var(--brand-navy, #002458);
}

.feature-row {
  display: contents; /* Align features with grid columns */
}

.feature-name-cell {
  background: #f9f9f9;
  font-weight: bold;
  padding: 15px;
  border-left: 1px solid #ddd; /* Add border to the first cell */
}
 
.feature-value-cell {
  background: white;
  text-align: center;
  padding: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.feature-value-cell span {
    font-weight: 700;
    font-size: 16px;
    color: #08a0a8;
}

/* Add border-bottom to the last row */
.feature-row:last-child .feature-name-cell,
.feature-row:last-child .feature-value-cell {
  border-bottom: 1px solid #ddd; /* Add bottom border to the last row */
}

.header-plan-cell:last-child,
.feature-value-cell:last-child {
  border-right: 1px solid #ddd; /* Add border to the last cell */
}

.check-icon {
  color: #08a0a8;
  font-size: 1.2em;
}

.dash-icon {
  color: #666666 !important;
  font-size: 1.2em;
}

.free-trial-btn {
  background-color: #08a0a8;
  border: 2px solid #08a0a8 !important;
  color: #fff !important;
  padding: 7px 20px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
}

.free-trial-btn:hover {
  opacity: 0.8 !important;
}

.free-trial-btn:hover {
  background-color: #078f96;
}

/* Free Trial + Buy Now side by side */
.plan-cta-row {
  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
}

.buy-now-btn {
  background-color: #002458;
  border: 2px solid #002458 !important;
  color: #fff !important;
  padding: 7px 20px;
  border-radius: 5px;
  cursor: pointer;
  text-decoration: none;
}

.buy-now-btn:hover {
  background-color: #001a42;
}

/* Mobile Layout */
.mobile-table {
  display: none;
}

.mobile-table .pricing-plan-card {
  border: 1px solid #ddd;
  margin-bottom: 20px;
  padding: 20px;
  background-color: white;
}

.plan-header {
  text-align: center;
  margin-bottom: 15px;
}

.features-list {
  list-style: none;
  padding: 0;
}

.features-list li {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.features-list li:last-child {
  border-bottom: none;
}

.check-icon {
  color: #08a0a8;
  font-size: 1.2em;
}

.dash-icon {
  color: #ddd;
  font-size: 1.2em;
}

/* Responsive Design */
@media (max-width: 768px) {
  .desktop-table {
    display: none;
  }

  .mobile-table {
    display: block;
  }
}

.price-business-wrapper h1 {
    font-weight: 700;
    font-size: 35px;
    color: #08a0a8;
    margin: 100px 0 10px;
}

.price-business-wrapper p {
    font-size: 16px;
    color: #333333;
    font-weight: 400;
    margin-bottom: 30px;
}

.quote-btn {
  background-color: #002458 !important;
  border: 2px solid #002458 !important;
  color: #fff !important;
  padding: 7px 20px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
}

.quote-btn:hover {
    background-color: #001a42 !important;
}

.price-quote-wrapper{
    margin: 50px 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 308px;
    background-color: #08a0a8;
}

.price-quote-wrapper h1 {
    font-weight: 700;
    font-size: 35px;
    color: #E1FFF1;
    margin-bottom: 10px;
}

.price-quote-wrapper p {
    font-size: 20px;
    color: #FFFFFF;
    font-weight: 400;
    margin-bottom: 30px;
}

@media screen and (max-width: 959px) {
  .price-quote-wrapper {
    height: 250px;
  }

  .price-quote-wrapper h1 {
    font-size: 1.5rem;
  }

  .price-quote-wrapper p {
    font-size: 1rem;
  }
}
</style>

